// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerFieldsStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, computed, observable, toJS, transaction } from 'mobx';
import {
  any,
  assign,
  assignIn,
  cloneDeep,
  clone,
  concat,
  contains,
  filter,
  find,
  findIndex,
  first,
  flatMap,
  flow,
  forEach,
  get,
  getOr,
  head,
  includes,
  invokeArgs,
  isEmpty,
  isFunction,
  isNil,
  isUndefined,
  map,
  omit,
  pick,
  reject,
  uniqBy
} from 'lodash/fp';

import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import advancedSettingFetcher from 'fetchers/Izenda.AdvancedSettingDataFetcher';
import uiState from 'logic/Izenda.UiState.js';
import CalculatedFieldStore from 'logic/Izenda.CalculatedFieldStore';
import dataModelFetcher from 'fetchers/Izenda.DataModelDataFetcher';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import filterDetailsConstants from 'logic/data/Izenda.FilterDetailsConfigConstants';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import localizer from 'share/localization/Izenda.Localizer';
import embededReportUtils from 'share/utils/Izenda.EmbededReportUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ReportDesignerFormatsConstants from 'logic/data/Izenda.ReportDesignerFormatsConstants';
import LoadingManager from 'modules/Izenda.LoadingManager';
import ReportWorker from 'worker-loader?inline&name=izenda_worker.js!share/workers/Izenda.ReportWorker.js';
import WORKER_CONSTANTS from 'share/workers/logic/data/Izenda.WorkerConstants';
import { getApiInfo } from 'share/workers/utils/Izenda.WorkerUtils';
import compare from 'share/utils/Izenda.PropsComparison';
import ColorPaletteStore from 'logic/Izenda.ColorPaletteStore';
import commonDataStore from './Izenda.ReportDesignerCommonDataStore';
import { DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE } from 'logic/data/Izenda.ReportPartFieldConstants';
import cache from 'share/utils/Izenda.CacheInAPISideAttribute';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
/**
 * ReportDesignerFieldsStore
 */

@cache
class ReportDesignerFieldsStore {
  @observable masterModel;
  @observable calculatedStore = new CalculatedFieldStore();
  @observable isLoading = false;
  @observable reportPartErrorMessages = [];
  @observable exploreRecords;
  @observable exploreStore;
  @observable needToExploreStoreReportInfoUpdate = false;
  constructor(reportStore: any, viewMode: boolean, reportState: number, exploreStore: any = null) {
    // master store
    this.reportStore = reportStore;
    this.viewMode = viewMode;
    this.tenantId = uiState.getTenantId();
    // Report info when get data for filter source
    this.reportState = reportState || constants.REPORT_STATE.POSTED;

    this.reportWorkerPendingList = [];
    this.reportWorkerPendingExecuted = [];
    this.previousPreviewData;

    /* main model of store */
    this.masterModel = reportStore.model;
    this.setUseCache();

    this.exploreStore = exploreStore;
    this.exploreRecords = exploreStore ? exploreStore.model.resultReportRecords : null;
    this.needToExploreStoreReportInfoUpdate = false;
  }

  @computed
  get model() {
    return this.reportStore.model.reportFields;
  }

  @computed
  get dataTree() {
    return this.model.dataTree;
  }

  @computed
  get reportFilters() {
    return this.model.reportFilter;
  }

  @computed
  get reportId() {
    return this.reportStore.reportId;
  }

  @computed
  get reportBody() {
    return this.model.reportBody;
  }

  @computed
  get reportHeader() {
    return this.model.reportHeader;
  }

  @computed
  get reportFooter() {
    return this.model.reportFooter;
  }

  @computed
  get reportFormats() {
    return this.reportStore.model.reportFormats;
  }

  @computed
  get fieldQuerySource() {
    let calculatedFieldDatSource = [];
    const querySource = toJS(getOr([], 'querySource', first(this.dataTree.details)));
    let visibleQuerySource = map(querySource => {
      querySource.filterFields = filter({ inaccessible: false }, querySource.filterFields);
      querySource.fields = filter({ inaccessible: false }, querySource.fields);
      return querySource;
    }, querySource);

    if (this.dataTree.details.length > 1) {
      calculatedFieldDatSource = toJS(this.dataTree.details[1].querySource);
    }

    return concat(visibleQuerySource.slice(), calculatedFieldDatSource);
  }

  @computed
  get displayFieldQuerySource() {
    const isHiddenDataType = field => {
      return filterDetailsConstants.HIDDEN_DATA_TYPE.indexOf(field.dataType) === -1;
    };

    return map(querySource => {
      querySource.filterFields = filter(isHiddenDataType, querySource.filterFields);
      querySource.fields = filter(isHiddenDataType, querySource.fields);

      return querySource;
    }, this.fieldQuerySource);
  }

  @computed
  get isVisibleHeaderFooter() {
    return this.reportFormats.headerFooter.visible;
  }

  @computed
  get isVisibleTitleDescription() {
    return this.reportFormats.titleDescription.visible;
  }

  /**
   * @property isEditingKPIReportPart
   * @description indicator for whether the current report being editted is a KPI report part
   */
  @computed
  get isEditingKPIReportPart() {
    return this.reportBody.configReportPart &&
      this.reportBody.configReportPart.type === REPORT_PART_TYPE.KPI;
  }

  @computed
  get reportTitleDescription() {
    return this.model.reportTitleDescription;
  }

  @computed
  get crossFilteringDrillFilterInfo() {
    return this.model.reportBody.crossFilteringDrillFilterInfo;
  }

  /**
   * Load available query source.
   * @param  {string} reportId
   * @return {promise}
   */
  loadAvailableQuerySourceFields(reportId: string) {
    const { dataTree, masterModel } = this;
    const data = reportId || masterModel.getReportDefinition();

    return fetcher.availableQuerySourceFields(data).then(result => {
      dataTree.readDataSource(result);
      return Promise.resolve(true);
    });
  }

  /**
   * Get filter data
   * @param  {any} version
   * @param  {any} value
   * @param  {boolean} fromDataSourcePage
   * @param  {function} updateFilterDataCallBack
   * @param  {boolean} editMode
   * @return {promise}
   */
  getFilterData(
    version: any,
    value: any,
    fromDataSourcePage: boolean,
    updateFilterDataCallBack: () => mixed,
    editMode: boolean
  ) {
    const dataTreeModel = this.dataTree;
    const reportFiltersModel = this.reportFilters;
    const reportId = this.reportId;
    const reportBody = this.model.reportBody;
    const data = fromDataSourcePage ? this.masterModel.getReportDefinition() : reportId;

    return Promise.join(
      fetcher.loadFilterOperatorGroup(),
      fromDataSourcePage ? fetcher.availableQuerySourceFields(data, this.reportStore.isLoadDraft) : null,
      !this.viewMode || editMode ? fetcher.loadFilterSource(data) : null, // Dont call this fetcher in View/Print mode
      action((filterOperatorGroup, availableQuerySourceFields, querySource) => {
        /* report filter */
        reportFiltersModel.populateOperatorGroup(filterOperatorGroup);
        !isNil(querySource) && reportFiltersModel.populateFilterQuerySource(querySource);
        reportFiltersModel.updateConfigFieldList(value, [], reportBody.viewMode);

        /* data source tree */
        !isNil(availableQuerySourceFields) && dataTreeModel.readDataSource(availableQuerySourceFields);
        isFunction(updateFilterDataCallBack) && updateFilterDataCallBack();

        return null;
      })
    )
      .then(() => {
        let promiseList = [];
        reportFiltersModel.configFieldList.forEach(configField => {
          if (
            contains(configField.filterInfo.sourceType, ['fieldSource', 'inTimePeriod']) ||
            contains(configField.filterInfo.operator.key, ['equals(checkbox)', 'notequal(checkbox)'])
          ) {
            promiseList.push(this.getOperatorValueSource(configField));
          }
        });

        return Promise.all(promiseList);
      })
      .then(() => {
        (!this.viewMode || editMode) && this.fillCalculatedFieldData();
        return true;
      });
  }

  /**
   * Load data for field or format page in report designer
   * @return {promise}
   */
  @action
  loadInitial() {
    const { dataTree, reportFilters, reportId, reportStore } = this;
    // Reset loadedDataSource when load inital data
    dataTree.loadedDataSource = false;
    return Promise.join(
      fetcher.loadFilterOperatorGroup(),
      fetcher.availableQuerySourceFields(reportId, this.reportStore.isLoadDraft),
      fetcher.loadFilterSource(reportId),
      action((filterOperatorGroup, availableQuerySourceFields, querySource) => {
        /* report filter */
        reportFilters.populateOperatorGroup(filterOperatorGroup);
        !isNil(querySource) && reportFilters.populateFilterQuerySource(querySource);

        // Populate relationship auto filters
        reportStore.syncRelationshipFilters();

        // when receive operator data, recheck disable cascading
        reportFilters.disableCascading();

        /* data source tree */
        !isNil(availableQuerySourceFields) && dataTree.readDataSource(availableQuerySourceFields);

        this.fillCalculatedFieldData();
      })
    );
  }

  /**
   * Fill calculated field data
   * @param {string} excludeFieldId
   * @param {Array<any>} fields
   * @return {promise}
   */
  fillCalculatedFieldData(excludeFieldId: string, fields: Array<any>, includeCustomJsonFunction: any) {
    const source = this.dataTree.details;
    if (source.length === 0 || !source[0].querySource || source[0].querySource.length === 0) {
      return;
    }

    const selectedDataSource = source[0].querySource[0];

    const fieldFilterById = (items, id) => {
      return filter(item => item.id !== excludeFieldId, items);
    };

    let dataSources = toJS(source[0].querySource);

    source.length > 1 && source[1].querySource && (dataSources = dataSources.concat(toJS(source[1].querySource)));

    // Load fields data
    if (!fields) {
      fields =
        selectedDataSource &&
        selectedDataSource.fields.map(field => {
          return {
            name: field.name,
            dataType: field.izendaDataType || field.dataType,
            description: field.expression
          };
        });
    }

    const selectedQuerySourceIds = map('id', source[0].querySource);

    return dataModelFetcher
      .fetchFunctionOperators(this.tenantId, selectedQuerySourceIds, includeCustomJsonFunction)
      .then(functionExpression => {
        this.calculatedStore.fillCalculatedFieldData(
          {
            databaseName: selectedDataSource.connectionName,
            fields: fieldFilterById(selectedDataSource.fields, excludeFieldId),
            id: selectedDataSource.id,
            name: selectedDataSource.name,
            schemaName: selectedDataSource.querySourceCategoryName
          },
          dataSources,
          functionExpression,
          this.reportId,
          fields
        );
      });
  }

  /**
   * Update current config field to pull data
   * @param {Object} filterField
   * @param {Object} configField
   */
  updateFieldDetailsForLoadOperatorValue(filterField: any, configField: any) {
    if (filterField.uniqueId !== configField.fieldDetails.uniqueId) return;
    const fieldDetail = omit(['filterItemDom', 'filterList'], configField.fieldDetails);
    return Object.assign(filterField, toJS(fieldDetail), { selected: true });
  }

  /**
   * List all of api for filter-operator
   * @param {any}  configField
   * @param {any}  reportFilter
   * @returns {promise}
   */
  getOperatorValueSource(configField: any, reportFilter: any) {
    const filterDetails = (configField && configField.filterInfo) || {};

    if (includes(get('operator.key', filterDetails), filterDetailsConstants.NO_POPULATE_IMMEDIATELY_FILTER_DATA)) {
      configField.populatedData = true;
      return;
    }
    const dataType = configField.fieldDetails.izendaDataType || configField.fieldDetails.dataType;
    const reportId = this.reportId;
    const sourceType = filterDetails.sourceType;
    const data = this.masterModel.getReportDefinition();
    data.reportState = this.reportState;

    if (!!reportFilter) {
      data.report.reportFilter = reportFilter;
      forEach(item => {
        item.value = null;
        item.id === configField.fieldDetails.id && (item.selected = true);
      }, data.report.reportFilter.filterFields);
    } else {
      // Set selected field
      forEach(item => {
        this.updateFieldDetailsForLoadOperatorValue(item, configField);
      }, data.report.reportFilter.filterFields);
    }

    const populateData = response => {
      configField.populateOperatorSourceValue(sourceType, response);
    };

    switch (sourceType) {
      case 'fieldSource':
        return fetcher.loadFilterDataSource(reportId, dataType).then(populateData);
      case 'inTimePeriod':
        return fetcher.loadTimeperiods().then(populateData);
      case 'dataSource':
        return fetcher.loadFilterFieldData(data).then(populateData);
      case 'dataSourceTree':
        return fetcher.loadFilterFieldData(data, true).then(populateData);
      default:
        // TODO - for testing
        return fetcher.loadFilterDataSource(reportId, dataType).then(populateData);
      // return Promise.resolve();
    }
  }

  /**
   * Load data source for filters
   * Use for individual filter in dashboard part.
   * @param {any} configFields
   * @param {any} reportFilter
   * @return {promise}
   */
  getOperatorValueSources(configFields: any, reportFilter: any) {
    const listPromise = [];

    forEach(item => {
      listPromise.push(this.getOperatorValueSource(item, cloneDeep(toJS(reportFilter))));
    }, configFields);

    return Promise.all(listPromise);
  }

  /**
   * Get partial data for filter value
   * @param  {any} pageInfo
   * @param  {any} configField
   * @param  {string} searchKey
   * @param  {boolean} isSearching
   * @param  {boolean} forDashboard
   * @return {promise}
   */
  @action
  getData(pageInfo: any, configField: any, searchKey: string, isSearching: boolean, forDashboard: boolean) {
    const data = this.masterModel.getReportDefinition();
    const filterDetails = (configField && configField.filterInfo) || {};
    forDashboard &&
      (data.report.reportFilter.filterFields = map(
        item => toJS(item.fieldDetails),
        this.reportFilters.configFieldList
      ));

    // Set selected field
    forEach(item => {
      this.updateFieldDetailsForLoadOperatorValue(item, configField);
    }, data.report.reportFilter.filterFields);

    data.reportState = this.reportState;

    const param = {
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      reportParameter: data,
      includeAll: false,
      includeBlank: false,
      criteria: [
        {
          key: 'filter',
          value: searchKey
        }
      ]
    };

    if (includes(filterDetails.operator.key, ['equals(popup)', 'notequal(popup)'])) {
      param.includeBlank = true;
    }
    if (
      includes(filterDetails.operator.key, [
        'equals(manualentry)',
        'equals(manualentrynoauto-complete)',
        'notequal(manualentry)',
        'notequal(manualentrynoauto-complete)',
        'equals(selection)',
        'notequal(selection)'
      ])
    ) {
      param.includeBlank = true;
      if (configField.fieldDetails.operatorSetting === filterDetailsConstants.SELECT_SETTINGS.MULTIPLE) {
        param.includeAll = true;
      }
    }

    return fetcher.loadPartialFilterFieldData(param).then(
      action(response => {
        configField.updateOperatorSourceDisplay(response, isSearching);
      })
    );
  }

  /**
   * Update cascading filter value
   * @param  {any} configFields
   * @return {promise}
   */
  @action
  updateCascadingValue(configFields: any) {
    const configFieldList = this.reportFilters.configFieldList || [];
    const filterIndex = configFieldList.indexOf(configFields);

    if (filterIndex < 0) {
      return Promise.resolve();
    }

    const promiseList = [];
    const isCascadingFilter = configFields.fieldDetails.cascading;
    if (isCascadingFilter) {
      for (let i = filterIndex + 1; i < configFieldList.length; i++) {
        promiseList.push(this.getOperatorValueSource(configFieldList[i]));
        configFieldList[i].fieldDetails.updateByKey('value', null);
        configFieldList[i].resetOperatorSourceDisplay();

        if (!configFieldList[i].fieldDetails.cascading) {
          break;
        }
      }
    }

    return Promise.all(promiseList);
  }

  /**
   * Update cascading filter value for Report Designer
   * @param  {any} fieldDetail
   */
  @action
  updateReportDesignerCascadingFilter(fieldDetail: any) {
    const filterFields = this.reportFilters.filterFields || [];
    const filterIndex = findIndex(filterField => filterField.uniqueId === fieldDetail.uniqueId, filterFields);

    if (filterIndex < 0) {
      return;
    }

    const isCascadingFilter = fieldDetail.cascading;
    if (isCascadingFilter) {
      for (let i = filterIndex + 1; i < filterFields.length; i++) {
        // Do not reset value of parameter filter field
        !filterFields[i].isParameter && filterFields[i].updateByKey('value', '');

        if (!filterFields[i].cascading) {
          break;
        }
      }
    }
  }

  /**
   * Update stored procedure parameter filter values
   * @param filters
   * - key: full field name
   * - value: new filter value
   */
  updateSpFilters(filters: { key: string, value: any }) {
    const spParameterFilters = filter(x => x.isParameter, this.reportFilters.filterFields);
    forEach(field => {
      // Calculate full field name:
      let fullFieldName = String.format('[{0}]', field.sourceFieldName);
      if (!field.isCalculated) {
        fullFieldName = String.format('[{0}].{1}', field.sourceDataObjectName, fullFieldName);
        if (!field.relationshipId && field.connectionName && field.querySourceCategoryName) {
          fullFieldName = String.format(
            '[{0}].[{1}].{2}',
            field.connectionName,
            field.querySourceCategoryName,
            fullFieldName
          );
        }
      }
      const foundFilter = find(x => x.key === fullFieldName, filters);
      if (foundFilter) {
        field.value = foundFilter.value;
      }
    }, spParameterFilters);
  }

  /**
   * Validate filter type in subReport
   * @param {any} subreport
   * @returns {promise}
   */
  validateFilterFieldsTypeSubReport(subReportData: any) {
    const reportFilter = this.model.reportFilter || {};
    const logic = reportFilter.logic;
    const subReport =
      subReportData || getOr({}, 'configReportPart.configField.properties.drillDown.subReport', this.reportBody);

    const reportId = subReport.selectedReport;
    const masterReportId = subReport.masterReportId || this.reportId;
    const inheritFilter = subReport.reportFilter;

    return !this.viewMode || logic
      ? fetcher.validateFilterFieldsTypeSubReport(reportId, masterReportId, inheritFilter)
      : Promise.resolve();
  }

  /**
   * Get warning message for ignored filters
   * @return {string}
   */
  getUnspecifiedFilterWarning() {
    const reportFilter = this.model.reportFilter || {};
    const { logic, filterFields } = reportFilter;
    let message = '';
    if (logic && filterFields) {
      const result = find(
        item => item.state !== constants.OBJECT_STATE.DELETE && logic.indexOf(item.position) < 0,
        filterFields
      );
      if (result) {
        message = constants.BUNDLE.MESSAGE.FILTER_WILL_BE_IGNORED;
      }
    }
    return message;
  }

  /**
   * Validate filter syntax
   * @returns {promise}
   */
  validateFilterSyntax() {
    const reportFilter = this.model.reportFilter || {};
    const { logic, filterFields } = reportFilter;
    const positions =
      filterFields &&
      flow(
        filter(item => item.state !== constants.OBJECT_STATE.DELETE),
        map(item => item.position)
      )(filterFields);

    const listFilterFields = flatMap(filter => {
      return pick(['alias', 'position', 'operatorId', 'isParameter', 'isForKeyJoin'], filter);
    }, filterFields);

    const filterTypeValidate = get('report.reportFilter', this.masterModel.getReportDefinition('Fields'));

    return logic
      ? fetcher.validateFilterFieldsType(filterTypeValidate).then(result => {
        if (result) {
          return constants.FIELDS.M24;
        } else {
          return fetcher.validateFilterSyntax(logic, positions, listFilterFields).then(result => result.messages);
        }
      })
      : Promise.resolve('');
  }

  /**
   * Validate fields
   * @returns {promise}
   */
  validateFields() {
    const reportStore = this.reportStore;
    const model = reportStore.model;
    const reportDesignerData = model.getReportDefinition();

    return fetcher.validate(reportDesignerData).then(
      result => {
        reportStore.setAlertDetails(result);
        return Promise.resolve(result.success);
      },
      error => {
        const errors = error.responseJSON;
        reportStore.setAlertDetails({
          errors: [errors],
          message: errors.message,
          success: false
        });
        return Promise.resolve(false);
      }
    );
  }

  /**
   * Load Initial Data For SubReport : Reports List, Fields
   * @param {string} selectedReportId
   * @return {promise}
   */
  loadInitialSubReports(selectedReportId: string) {
    const configReportPart = this.model.reportBody.configReportPart;

    return Promise.join(
      fetcher.loadFilterSource(selectedReportId),
      fetcher.loadForEdit(selectedReportId, this.reportStore.isLoadDraft),
      (filterSource, reportData) => {
        return {
          fieldInCurrentReportPart: configReportPart.visibleFieldCollection,
          fieldInSubReport: filterSource,
          reportData: reportData.report || {}
        };
      }
    );
  }

  /**
   * Load Subreport data
   * @param {string} reportId
   * @return {promise}
   */
  loadSubReportData(reportId: string) {
    return Promise.join(
      fetcher.loadForEdit(reportId, this.reportStore.isLoadDraft),
      fetcher.loadFilterSource(reportId),
      (reportData, filterSource) => {
        return {
          reportData: reportData.report || {},
          fieldInSubReport: filterSource
        };
      }
    );
  }

  /**
   * Get alertDetails from report store
   * @return {any}
   */
  getAlertDetail() {
    return this.reportStore.alertDetails;
  }

  /**
   * Add calculated field and bind result back to report middle panel and fill new data into calculated field
   * @param {any} calculatedData
   * @returns {promise}
   */
  async addCalculatedField(calculatedData: any) {
    calculatedData = calculatedData || {};
    const data = {
      reportKey: {
        key: this.reportId
      },
      calculatedField: {
        id: calculatedData.id || null,
        name: calculatedData.name,
        functionName: calculatedData.functionName,
        expression: calculatedData.expression,
        izendaDataType: calculatedData.izendaDataType
      }
    };

    const result = await fetcher.addCalculatedField(data);
    result.success !== false && this.dataTree.readCalculatedDataSource(result, true);
    await this.fillCalculatedFieldData(result);
    await this.getFilterSources();

    const updatedCalField = find({ id: data.calculatedField.id }, result.querySource[0].fields);
    if (this.reportBody.updateElementInfo(data.calculatedField.id, data.calculatedField.name, updatedCalField)) {
      this.updateReportsParts();
    }
    return true;
  }

  /**
   * Delete calculated field
   * @param {any} calculatedData
   * @returns {promise}
   */
  @action
  deleteCalculatedField(calculatedData: any) {
    calculatedData = calculatedData || {};
    const data = {
      reportKey: {
        key: this.reportId
      },
      calculatedField: {
        id: calculatedData.id || null
      }
    };
    return fetcher
      .deleteReportCalculatedField(data)
      .then(
        action(result => {
          result && this.dataTree.deleteCalculatedField(calculatedData);
          return result;
        })
      )
      .then(this.getFilterSources.bind(this));
  }

  /**
   * update data request common for both view mode and edi mode
   * @param {any} request
   * @param {any} reportPart
   * @param {any} drillDownData
   * @param {number} pageIndex
   * @returns {promise}
   */
  buildCommonParamsGetDataRequest(request: any, reportPart: any, drillDownData: any, pageIndex: number) {
    // Common params for both mode
    const reportPartContent = reportPart.reportPartContent;
    const optionalParams = reportPartContent.optionalParams || {};

    // pagination
    if (
      reportPartContent.usePagination ||
      get('properties.usePagination', reportPartContent) ||
      get('properties.view.usePagination', reportPartContent)
    ) {
      optionalParams.paging = {
        pageIndex: pageIndex || constants.DEFAULT_PAGE_INDEX,
        pageSize: reportPartContent.properties.pageSize || constants.DEFAULT_PAGE_SIZE
      };
    }

    request = assign(request, optionalParams);

    drillDownData = drillDownData || invokeArgs('getDrilldownInfo', [this.masterModel.printingMode], reportPartContent);

    if (reportPartContent.isCrossFiltering) {
      Array.prototype.push.apply(
        drillDownData.filters,
        ReportPartUtils.transformFilterWithReportPart(reportPart, this.reportBody.crossFilteringDrillFilterInfo)
      );
      drillDownData.filters = uniqBy(
        item => JSON.stringify(pick(['key', 'fieldId', 'value', 'dataFormat'], item)),
        drillDownData.filters
      );
    }

    if (drillDownData) {
      request = this.getDrillDownData(request, drillDownData);
    }

    request = assign(request, omit(['overridingFilterValue', 'filters'], this.masterModel.subReportFilter));
    request.filters = concat(request.filters || [], getOr([], 'filters', this.masterModel.subReportFilter));

    // in print mode we will get all data and ignore usePagination option
    if (this.masterModel.printingMode) {
      request = omit(['paging'], request);
    }

    return request;
  }

  /**
   * build data request for edit mode. System consider edit mode when report designer model is dirty
   * @param {any} reportPart
   * @param {any} drillDownData
   * @param {number} pageIndex
   * @param {boolean} ignoreCache
   * @returns {promise}
   */
  buildRequestDataForEditMode(
    reportPart: any,
    drillDownData: any,
    pageIndex: number,
    useForThreshold: boolean,
    ignoreCache: boolean
  ) {
    // get data for
    const data = this.masterModel.getReportDefinition('Fields');

    // Add ignore cache
    data.ignoreCache = ignoreCache;

    // Filter report part, only send data for request report part
    data.report.reportPart = flow(
      filter({
        title: reportPart.headerTitle
      }),
      reject({
        state: ObjectStateConstants.DELETE
      })
    )(data.report.reportPart);
    if (useForThreshold) {
      const newReportPart = head(data.report.reportPart);
      newReportPart.reportPartContent = assignIn(newReportPart.reportPartContent, {
        customContainers: reportPart.reportPartContent.customContainers
      });
      data.report.reportPart.splice(0, 1, newReportPart);
    }

    data.title = reportPart.headerTitle;

    // Remove reportRelationships in Simple mode
    if (!data.report.advancedMode) {
      data.report.reportRelationship = [];
    }
    return this.buildCommonParamsGetDataRequest(data, reportPart, drillDownData, pageIndex);
  }

  /**
   * build data request for view mode. System consider edit mode when report designer model is not diry
   * @param {any} reportPart
   * @param {any} drillDownData
   * @param {number} pageIndex
   * @param {boolean} ignoreCache
   * @returns {promise}
   */
  buildRequestDataForViewMode(reportPart: any, drillDownData: any, pageIndex: number, ignoreCache: boolean) {
    const data = {
      loadDraft: this.reportStore.isLoadDraft,
      reportId: this.reportId,
      reportPartId: reportPart.id,
      tenantId: uiState.getTenantId(),
      ignoreCache: ignoreCache,
      userAction: this.viewMode
        ? localizer.getText('REPORTDESIGNER_LOGGING_VIEWER_MODE')
        : localizer.getText('REPORTDESIGNER_LOGGING_MODE')
    };

    data.overridingFilterValue = get('overridingFilterValue', this.masterModel.subReportFilter);

    return this.buildCommonParamsGetDataRequest(data, reportPart, drillDownData, pageIndex);
  }

  /**
   * get Report part data, only return promise that have data not modify report part object, common method for all get report part data
   * @param  {any} reportPart
   * @param  {any} drillDownData
   * @param  {number} pageIndex
   * @return {promise}
   */
  getReportPartData = LoadingManager.addLoadingProgress(fetcher, (loader, fetcher) => {
    return (reportPart: any, drillDownData: any, pageIndex: number) => {
      let data = null;
      let fetcherPromise = null;
      // if master model is dirty we will use edit mode api, where we send all the report definition
      // if we use draft data we also need to send draft data definition
      if (this.masterModel.checkDirty() || this.masterModel.draftId || !this.masterModel.active) {
        data = this.buildRequestDataForEditMode(reportPart, drillDownData, pageIndex);
        fetcherPromise = fetcher.getConfigReportPartPreviewData;
      } else {
        data = this.buildRequestDataForViewMode(reportPart, drillDownData, pageIndex);
        fetcherPromise = fetcher.getPreviewData;
      }

      // When this report part is used for exporting or printing, we set this flag so that the No. records loaded
      // should be adjusted for the purpose this actions
      if (this.masterModel.printingMode) {
        data.loadAll = true;
      }

      return fetcherPromise(data);
    };
  });

  /**
   * get Report part data except Chart, only return promise that have data not modify report part object, common method
   * @param  {any} data
   * @return {promise}
   */
  getReportPartDataForReportPart = LoadingManager.addLoadingProgress(fetcher, (loader, fetcher) => {
    return (data: any) => {
      let fetcherPromise = null;
      // if master model is dirty we will use edit mode api, where we send all the report definition
      // if we use draft data we also need to send draft data definition
      if (this.masterModel.checkDirty() || this.masterModel.draftId || !this.masterModel.active) {
        fetcherPromise = fetcher.getConfigReportPartPreviewData;
      } else {
        fetcherPromise = fetcher.getPreviewData;
      }
      // When this report part is used for exporting or printing, we set this flag so that the No. records loaded
      // should be adjusted for the purpose this actions
      if (this.masterModel.printingMode) {
        data.loadAll = true;
      }
      return fetcherPromise(data);
    };
  });

  /**
   * get DrillDown Record for Grid Pivot/DrillDown This method only return raw data not modify report part object
   * @param  {any} reportPart
   * @param  {any} drillDownOption
   * @param  {number} pageIndex
   * @return {promise}
   */
  getDrillDownRecords(reportPart: any, drillDownOption: any, pageIndex: number) {
    const configReportPart = reportPart || (this.model.reportBody && this.model.reportBody.configReportPart);
    if (!configReportPart || !configReportPart.isBeingBuild) {
      configReportPart && configReportPart.setQueryData && configReportPart.setQueryData({});
      return Promise.resolve(false);
    }
    return this.getReportPartData(reportPart, drillDownOption, pageIndex);
  }

  /**
   * needUpdatePreview function is to get the data source to manage to show it in the preview
   * @param  {any} reportPart
   * @param  {any} drillDownOption
   * @param  {number} pageIndex
   * @param  {boolean} isHiddenLoading
   * @param  {boolean} isPrintMode
   * @param  {boolean} notUpdateFilter
   * @return {promise}
   */
  @action
  async needUpdatePreview(
    reportPart: any,
    drillDownData: any,
    pageIndex: boolean,
    isHiddenLoading: boolean,
    isPrintMode: boolean,
    notUpdateFilter: boolean,
    forceUpdate: boolean,
    ignoreCache: boolean = false
  ) {
    let configReportPart = reportPart || (this.model.reportBody && this.model.reportBody.configReportPart);

    !notUpdateFilter && this.model.reportBody.updateCrossFiltering(configReportPart, drillDownData);

    let data, type;

    if (
      this.masterModel.checkDirty() ||
      this.masterModel.draftId ||
      !this.masterModel.active ||
      configReportPart.useForThreshold ||
      this.needToExploreStoreReportInfoUpdate
    ) {
      data = this.buildRequestDataForEditMode(
        configReportPart,
        drillDownData,
        pageIndex,
        configReportPart.useForThreshold,
        ignoreCache
      );
      type = 'REPORT_PART_REVIEW_DATA_CONFIG';
    } else {
      data = this.buildRequestDataForViewMode(configReportPart, drillDownData, pageIndex, ignoreCache);
      type = 'REPORT_PART_REVIEW_DATA';
    }

    const postData = JSON.parse(JSON.stringify(data));

    const haveRefresh = configReportPart && !isNil(configReportPart.refreshDataProcess);
    if (
      !haveRefresh &&
      this.previousPreviewData &&
      compare(this.previousPreviewData, postData) &&
      !forceUpdate &&
      !postData.ignoreCache &&
      !this.exploreStore
    ) {
      return Promise.resolve(false);
    }

    this.previousPreviewData = postData;
    // TODO: this check will be removed when all of report part types have lazy loading.
    // Currently, we run this check because QA cannot test with others part type.
    if (
      any(type => configReportPart.reportPartContent.type === type, [
        constants.REPORT_PART_TYPE.Chart,
        constants.REPORT_PART_TYPE.Map,
        constants.REPORT_PART_TYPE.Gauge,
        constants.REPORT_PART_TYPE.KPI
      ])
    ) {

      if (!configReportPart || !configReportPart.isBeingBuild) {
        configReportPart && configReportPart.setQueryData && configReportPart.setQueryData({});
        return Promise.resolve(false);
      } else {
        // if master model is dirty we will use edit mode api, where we send all the report definition
        // if we use draft data we also need to send draft data definition
        configReportPart.setQueryData({}, true);

        // When this report part is used for exporting or printing, we set this flag so that the No. records loaded
        // should be adjusted for the purpose this actions
        this.masterModel.printingMode && (data.loadAll = true);
        const colorPaletteStore = new ColorPaletteStore();

        if (isEmpty(configReportPart.colorPalettes)) {
          configReportPart.colorPalettes = await colorPaletteStore.loadThemes();
        }

        // need to explorer root js
        if (this.exploreRecords && this.exploreStore && !this.needToExploreStoreReportInfoUpdate) {
          const result = this.exploreRecords;
          // const reportStore = this.reportStore;
          // populate data or post error message to report part
          reportPart.setQueryData && reportPart.setQueryData(result);

          return Promise.resolve({});
        }
        this.createReportPartWorker(configReportPart, data, type);
        return Promise.resolve({});
      }
    } else {
      if (this.exploreStore && this.needToExploreStoreReportInfoUpdate) {
        this.exploreStore.updateGetVisualizationRequest(postData.report);
      }
      return this.needUpdatePreviewForGridForm(
        data,
        reportPart,
        drillDownData,
        pageIndex,
        isHiddenLoading,
        isPrintMode,
        notUpdateFilter
      );
    }
  }

  /**
   * deletePreviousPreviewData is to clear the old report part preview data after deleting the report part
   */
  deletePreviousPreviewData() {
    this.previousPreviewData = null;
  }

  /**
   * needUpdatePreview function is to get the data source to manage to show it in the preview
   * @param  {any} data
   * @param  {any} reportPart
   * @param  {any} drillDownOption
   * @param  {number} pageIndex
   * @param  {boolean} isHiddenLoading
   * @param  {boolean} isPrintMode
   * @param  {boolean} notUpdateFilter
   * @param  {any} updateReviewData
   * @return {promise}
   */
  needUpdatePreviewForGridForm = LoadingManager.addLoadingProgress(fetcher, (loader, fetcher) => {
    return (
      data: any,
      reportPart: any,
      drillDownData: any,
      pageIndex: number,
      isHiddenLoading: boolean,
      isPrintMode: boolean,
      notUpdateFilter: boolean,
      updateReviewData: any
    ) => {
      const self = this;
      const configReportPart = reportPart || (this.model.reportBody && this.model.reportBody.configReportPart);
      if (!configReportPart || !configReportPart.isBeingBuild) {
        configReportPart && configReportPart.setQueryData && configReportPart.setQueryData({});
        return Promise.resolve(false);
      }

      !notUpdateFilter && this.model.reportBody.updateCrossFiltering(configReportPart, drillDownData);
      !isHiddenLoading && (this.isLoading = true);

      const reportStore = this.reportStore;
      configReportPart.startLoadData();

      const embededReportDefPromise = embededReportUtils.buildPromiseFetcher(
        configReportPart,
        fetcher.loadReportPartDefinition.bind(fetcher),
        fetcher.loadForEdit.bind(fetcher),
        this.viewMode
      );

      if (this.exploreRecords && this.exploreStore && !this.needToExploreStoreReportInfoUpdate) {
        return new Promise(resolve => {
          transaction(() => {
            configReportPart.setQueryData(self.exploreRecords);
          });
          resolve(false);
        });
      };


      return Promise.join(
        this.getReportPartDataForReportPart.apply(this, concat(loader ? [loader] : [], [data])),
        embededReportDefPromise,
        (result, embededReportDef) => {
          if (!result.messages) {
            this.populateReportPart(result, configReportPart);
            transaction(() => {
              configReportPart.setQueryData(result);
              configReportPart.readEmbededReportDef(embededReportDef);
              if (self.exploreStore) {
                self.exploreStore.updateReportDefnitionInfo(data);
              }
              if (self.exploreStore && self.needToExploreStoreReportInfoUpdate) {
                self.exploreStore.updateGetVisualizationRecords(result);
                self.resetneedToExploreStoreReportInfoUpdate();
              }
            });
          } else {
            if (result.messages[0].key === constants.ERROR_KEY.reportPart) {
              this.reportPartErrorMessages.push({
                reportPartId: configReportPart.id,
                message: result.messages[0].messages[0]
              });
              reportStore.setAlertDetails({
                errors: [
                  {
                    message: localizer.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_INVALID_REPORTPART')
                  }
                ],
                success: false
              });
            } else {
              const keys = [
                constants.ERROR_KEY.calculatedfield,
                constants.ERROR_KEY.filter,
                constants.ERROR_KEY.relationship
              ];
              const key = includes(result.messages[0].key, keys) ? 'report' : null;
              reportStore.setAlertDetails({
                errors: [
                  {
                    message: find({ key: '' }, result.messages).messages[0]
                  }
                ],
                success: false,
                key: key
              });
            }
            configReportPart.setQueryData({});
          }
          return result.messages || null;
        }
      )
        .catch(error => {
          const errors = error.responseJSON || {};
          reportStore.setAlertDetails({
            errors: [errors],
            message: errors.message,
            success: false
          });
          configReportPart.setQueryData({});
          return false;
        })
        .finally(() => {
          !isHiddenLoading && (this.isLoading = false);
        });
    };
  });

  /**
   * Load data for related report parts by changed of cross filtering
   * @param {string} excludedPartId
   */
  loadReportPartDataByCrossFiltering(excludedPartId: string) {
    // remove waiting executed cross-filter report part
    this.reportWorkerPendingExecuted = filter(reportWorkerPending => {
      return !reportWorkerPending.reportPart.isCrossFiltering;
    }, this.reportWorkerPendingExecuted);

    // Terminate cross-filter report part in process
    forEach(reportPending => {
      if (reportPending.reportPart.isCrossFiltering && excludedPartId !== reportPending.reportPart.id) {
        reportPending.worker.terminate();
      }
    }, this.reportWorkerPendingList);

    // remove cross-filter report part in process
    this.reportWorkerPendingList = filter(reportWorkerPending => {
      return !reportWorkerPending.reportPart.isCrossFiltering;
    }, this.reportWorkerPendingList);

    // apply worker list here
    flow(
      filter(part => part.reportPartContent.isCrossFiltering && part.id !== excludedPartId),
      forEach(part => {
        const drillInfo = part.reportPartContent.getDrilldownInfo();
        Array.prototype.push.apply(
          drillInfo.filters,
          ReportPartUtils.transformFilterWithReportPart(part, this.reportBody.crossFilteringDrillFilterInfo)
        );
        drillInfo.filters = uniqBy(
          item => JSON.stringify(pick(['key', 'fieldId', 'value', 'dataFormat'], item)),
          drillInfo.filters
        );
        this.needUpdatePreview(part, drillInfo);
      })
    )(this.model.reportBody.activeReportParts);
  }

  /**
   * Remove cross filtering items belong to report part.
   * @param {Object} reportPart
   */
  removePillByReportPart(reportPart: any) {
    this.model.reportBody.removePillByReportPart(reportPart);
    this.loadReportPartDataByCrossFiltering();
  }

  /**
   * Remove a cross filtering item.
   * @param {any} pill
   */
  removePill(pill: any) {
    this.model.reportBody.removePill(pill);
    this.loadReportPartDataByCrossFiltering();
  }

  /**
   * Remove all cross filtering item.
   */
  removeAllPills() {
    this.model.reportBody.removeAllPills();
    this.loadReportPartDataByCrossFiltering();
  }

  /**
   * Get random preview data by expanded level
   * @param {any} expandedLevel
   */
  getRandomPreviewData(expandedLevel: number) {
    const data = this.masterModel.getReportDefinition('Fields');
    data.title = localizer.getText('COMMON_LABEL_CHART');
    data.expandedLevel = expandedLevel;
    data.filters = null;
    return fetcher.getRandomPreviewData(data);
  }

  /**
   * getDrillDownData function is to get the drill down data to manage to show it in the preview
   * @param {any} data
   * @param {any} drillDownData
   * @return report definition object.
   */
  getDrillDownData(data: any, drillDownData: any) {
    data.filters = drillDownData.filters || [];
    data.expandedLevel = !isNil(drillDownData.expandedLevel) ? drillDownData.expandedLevel : data.filters.length;
    data.groupTotalValues = drillDownData.groupTotalValues;
    return data;
  }

  /**
   * Load definition of report part.
   * @param  {string} id
   * @return {promise}
   */
  loadReportPartDefinition(id: string) {
    return fetcher.loadReportPartDefinition(id);
  }

  /**
   * Update all report parts , use for Update Results button
   * @param {any} loader
   * @return {promise}
   */
  @action
  updateReportsParts(ignoreCache: boolean = false, reportPartsToUpdate: any[] = null, needToExploreStoreReportInfoUpdate = false) {
    this.needToExploreStoreReportInfoUpdate = needToExploreStoreReportInfoUpdate;
    const model = this.model;
    const reportBody = model.reportBody || {};

    // reset all filters
    !ignoreCache && reportBody.removeAllPills();

    flow(
      reject({ type: undefined }),
      forEach(reportPart => {
        this.needUpdatePreview(reportPart, null, null, null, null, null, null, ignoreCache);
      })
    )(reportPartsToUpdate || reportBody.activeReportParts);
    return Promise.resolve({});
  }

  /**
   * Get filter sources , add cuclated and aggregated field
   * @return {promise}
   */
  @action
  getFilterSources() {
    return fetcher
      .loadFilterSource(this.reportId)
      .then(this.reportFilters.populateFilterQuerySource.bind(this.reportFilters));
  }

  /**
   * Toggle snap to grid
   * @return {promise}
   */
  @action
  toggleSnapToGrid() {
    const reportBody = this.reportBody;
    reportBody.snapToGrid = !reportBody.snapToGrid;
    reportBody.markDirty();
  }

  /**
   * Load report filter
   * @return {promise}
   */
  @action
  loadReportFilter() {
    return fetcher.loadFilterOfReport(this.reportId).then(result => {
      this.model.reportFilter.populateFilterList(result.filterFields);
      return result.filterFields;
    });
  }

  /**
   * Get default image url
   * @param {string}
   * @return {promise}
   */
  loadDefaultImageUrl(tenantId: string) {
    return advancedSettingFetcher.getDefaultImageUrl(tenantId).then(result => {
      const imageUrl = get('value', result);
      if (!isEmpty(imageUrl)) {
        const imageItems = this.reportHeader.formatItems.filter(item => item.type === 'image');
        imageItems.forEach(item => {
          if (item.imageUrl === ReportDesignerFormatsConstants.ITEMS_TYPE.image.imageUrl || isEmpty(item.imageUrl)) {
            item.imageUrl = result.value;
          }
        });
      }
      this.masterModel.defaultImageUrl = result;
    });
  }

  /**
   * Clean cross filter setting
   */
  @action
  resetCrossFilterDefaultValue() {
    this.removeAllPills();
    this.reportBody.cleanCrossFiltering();
  }
  /**
   * reset needToExploreStoreReportInfoUpdate property so from explore store report part not update after this
   */
  resetneedToExploreStoreReportInfoUpdate() {
    this.needToExploreStoreReportInfoUpdate = false;
    this.exploreRecords = null;
  }

  setNeedToExploreStoreReportInfoUpdate(value: Boolean) {
    this.needToExploreStoreReportInfoUpdate = value;
  }

  /**
   * Create report part worker.
   * @param {any} reportPart
   * @param {any} data
   * @param {number} type
   */
  createReportPartWorker(reportPart: any, data: any, type: Number) {
    const currentIndex = findIndex(worker => worker.reportPart.id === reportPart.id, this.reportWorkerPendingList);

    // Get request data here
    const getPostData = () => {
      return {
        type: type,
        requestData: JSON.parse(JSON.stringify(data)),
        apiInfo: getApiInfo()
      };
    };

    // Terminate worker in list if another worker call
    if (currentIndex > -1) {
      const worker = this.reportWorkerPendingList[currentIndex].worker;
      worker.terminate();
      this.reportWorkerPendingList.splice(currentIndex, 1);
    }

    if (typeof Worker !== 'undefined') {
      if (this.reportWorkerPendingList.length < WORKER_CONSTANTS.WORKER_CHUNK) {
        const reportWorker = new ReportWorker();
        reportWorker.onmessage = this.populateReportPartData.bind(this, reportWorker, reportPart);
        reportWorker.postMessage(getPostData());

        // Add report worker list which is used for drilldown
        this.reportWorkerPendingList.push({
          reportPart: reportPart,
          worker: reportWorker
        });
      } else {
        this.reportWorkerPendingExecuted.push({
          reportPart: reportPart,
          data: data,
          type: type
        });
      }
    }
  }

  /**
   * Populate report part
   * @param {any} report
   * @param {any} reportPart Target report part
   */
  populateReportPart(data: any, targetReportPart: any) {
    const reportDefinition = data.reportDefinition;
    if (!reportDefinition) return;

    this.handleCurrentReportPartState(() => {
      const isAdvancedMode = this.masterModel.dataSource.reportMode === constants.ADVANCED;
      this.masterModel.updateReportInfo(
        reportDefinition.id,
        reportDefinition,
        isAdvancedMode,
        true,
        targetReportPart
      );

      this.getFilterData(0, null, null, null, this.reportStore.editMode);
    }, reportDefinition);
  }

  /**
   * Handle current report part state
   * @param {any} updateHandler
   * @param {any} reportDefinition
   */
  handleCurrentReportPartState(updateHandler: any, reportDefinition: any) {
    this.prohibitReloadData = true;

    const reportBody = get('model.reportBody', this);
    const crossFilteringDetail = [];
    forEach(crossFilteringItem => {
      var pillDefinitions = [];
      forEach(pillDefinition => pillDefinitions.push(pillDefinition), crossFilteringItem.pillDefinitions);

      crossFilteringDetail.push({
        reportId: crossFilteringItem.reportId,
        reportName: crossFilteringItem.reportName,
        pillDefinitions: pillDefinitions
      });
    }, reportBody.crossFilteringDetail);
    const crossFilteringDrillFilterInfo = reportBody.crossFilteringDrillFilterInfo;

    const reportParts = getOr([], 'reportParts', reportBody);
    const tempStateItems = {};
    forEach(reportPart => {
      tempStateItems[reportPart.id] = reportPart.getTemporaryStateData();
    }, reportParts);

    const newParamFilters = [];
    const filterFields = getOr([], 'model.reportFilter.filterFields', this);
    forEach(filterField => {
      if (filterField.isParameter && filterField.state === constants.OBJECT_STATE.INSERT
        && isNil(filterField.id)) { // filterField.id is null when filter is not processed yet
        newParamFilters.push(filterField);
      }

      // remove deleted subkey join filters
      if (filterField.isForKeyJoin && filterField.state === constants.OBJECT_STATE.DELETE) {
        const filters = getOr([], 'reportFilter.filterFields', reportDefinition);
        const fieldIndex = findIndex(item => {
          return item.id === filterField.id;
        }, filters);

        if (fieldIndex === -1) return;

        filters.splice(fieldIndex, 1);
      }
    }, filterFields);

    // at the moment, access rights cannot be changed in OnPreExecute
    // and because of this we need to restore them after update
    const accessList = getOr([], 'masterModel.accesses', this);

    updateHandler();

    const updatedFilterFields = getOr([], 'model.reportFilter.filterFields', this);
    forEach(newParamFilter => {
      updatedFilterFields.push(newParamFilter);
    }, newParamFilters);

    this.masterModel.reportAccess.accessList = accessList;

    forEach(reportPart => {
      const tempStateData = tempStateItems[reportPart.id];
      if (!isNil(tempStateData))
        reportPart.restoreTemporaryStateData(tempStateData);
    }, reportParts);

    reportBody.crossFilteringDetail = crossFilteringDetail;
    reportBody.crossFilteringDrillFilterInfo = crossFilteringDrillFilterInfo;

    this.prohibitReloadData = false;
  }

  /**
   * Populate report part data
   * @param {any} reportWorker
   * @param {any} reportPart
   * @param {any} event
   */
  populateReportPartData(reportWorker: any, reportPart: any, event: any) {
    const result = event.data;
    const reportStore = this.reportStore;
    // populate data or post error message to report part
    if (!result.messages) {
      this.populateReportPart(result, reportPart);
      reportPart.setQueryData && reportPart.setQueryData(result);
    } else {
      if (result.messages[0].key === constants.ERROR_KEY.reportPart) {
        this.reportPartErrorMessages.push({
          reportPartId: reportPart.id,
          message: result.messages[0].messages[0]
        });
        reportStore.setAlertDetails({
          errors: [
            {
              message: localizer.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_INVALID_REPORTPART')
            }
          ],
          success: false
        });
      } else {
        const keys = [
          constants.ERROR_KEY.calculatedfield,
          constants.ERROR_KEY.filter,
          constants.ERROR_KEY.relationship
        ];
        const key = includes(result.messages[0].key, keys) ? 'report' : null;
        reportStore.setAlertDetails({
          errors: [
            {
              message: find({ key: '' }, result.messages).messages[0]
            }
          ],
          success: false,
          key: key
        });
      }
      reportPart.setQueryData({});
    }

    // remove pending report worker in list
    this.reportWorkerPendingList.splice(
      findIndex(worker => worker.reportPart.id === reportPart.id, this.reportWorkerPendingList),
      1
    );

    // terminate reportWorker
    reportWorker.terminate();

    // start other worker if has
    if (this.reportWorkerPendingExecuted.length > 0) {
      let waitingReportPart = this.reportWorkerPendingExecuted[0];
      this.reportWorkerPendingExecuted.splice(0, 1);
      this.createReportPartWorker(waitingReportPart.reportPart, waitingReportPart.data, waitingReportPart.type);
    }
  }

  /**
   * Terminate AllReportPartWorker
   */
  @action
  terminateAllReportPartWorker(partsToRender: any[]) {
    // Terminate all pending worker
    this.reportWorkerPendingExecuted = [];
    forEach(reportPending => {
      if (partsToRender) {
        if (!isNil(find({ id: reportPending.reportPart.id }, partsToRender))) {
          reportPending.worker.terminate();
        }
      } else {
        reportPending.worker.terminate();
      }
    }, this.reportWorkerPendingList);

    // remove waiting list
    this.reportWorkerPendingList = [];
  }

  /**
   * Reset the function and format setting of all fields inside a report part content
   * @param {Object} reportPartContent Report part content
   */
  @action
  resetFunctionFormat(reportPartContent: Object): Promise {
    const { valuesElements, values } = reportPartContent;
    const getFunction = ({ functionData, formatData }) => {
      if (functionData) {
        functionData.defaultFormat = formatData
          ? {
            formatId: formatData.id,
            format: formatData.format,
            groupBy: formatData.groupBy,
            formatDataType: formatData.formatDataType
          }
          : '';
        return functionData;
      }
      return {};
    };

    const setElementFormatAndFunction = (element, info) =>
      action(formatData => {
        element.properties.setFormat({
          formatId: formatData.id || null,
          format: formatData.format || null,
          groupBy: formatData.groupBy || null,
          formatDataType: formatData.formatDataType || null
        });

        return ReportPartUtils.setFunctionElement(
          reportPartContent,
          element,
          omit('defaultFormat', info) || {},
          true /* manualChange */,
          true /* keep name */
        );
      });

    return Promise.all(
      valuesElements.map(element =>
        ReportPartUtils.getDefaultFunctionFormat(reportPartContent, values, element)
          .then(getFunction)
          .then(info => {
            const defaultFormatName = get(
              [element.fieldDataType, info.name].join('.'),
              DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE
            );
            const defaultFormatPromise = defaultFormatName
              ? commonDataStore.loadReportFieldDataFormat(element.fieldDataType).then(
                flow(
                  find({ format: defaultFormatName }),
                  defaults({})
                )
              )
              : Promise.resolve({});

            return defaultFormatPromise.then(setElementFormatAndFunction(element, info));
          })
      )
    );
  }
}

export default ReportDesignerFieldsStore;


// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportDesignerFieldsStore.js